"use client";

import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { getStoredSession, setToken, logout as authLogout, getMe } from "@/lib/auth";
import { hasFullyVerifiedContacts } from "@/lib/userVerification";
import {
    claimGuestCartToCloud,
    clearGuestCartStorage,
    clearLegacyCartStorage,
    readGuestCartFromStorage,
} from "@/lib/cart";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [user, setUser] = useState(null);
    const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
    const [authModalMode, setAuthModalMode] = useState("login");
    const [isProfileSettingsOpen, setIsProfileSettingsOpen] = useState(false);
    const [toastMessage, setToastMessage] = useState(null);
    const [authReady, setAuthReady] = useState(false); // true once client session is checked

    const fetchUser = useCallback(async () => {
        try {
            const response = await getMe();
            const userData = response?.data || response?.user || null;
            setUser(userData);
        } catch (err) {
            // 401 = invalid/expired token, 404 = user deleted — clear session
            if (err?.status === 401 || err?.status === 404) {
                setIsAuthenticated(false);
                setUser(null);
                authLogout();
            } else {
                setUser(null);
            }
        }
    }, []);

    useEffect(() => {
        let isCancelled = false;

        async function bootstrapAuth() {
            await Promise.resolve();
            if (isCancelled) {
                return;
            }

            const session = getStoredSession();
            if (session?.token) {
                setIsAuthenticated(true);
                fetchUser();
            }
            setAuthReady(true);
        }

        bootstrapAuth();

        return () => {
            isCancelled = true;
        };
    }, [fetchUser]);

    const claimGuestCartAfterLogin = useCallback(async () => {
        const guestCart = readGuestCartFromStorage();
        const hasGuestTemplate = Boolean(guestCart?.template?.id || guestCart?.template?.slug);
        if (!hasGuestTemplate) {
            return;
        }

        try {
            await claimGuestCartToCloud(guestCart, "LATEST_WINS");
            clearGuestCartStorage();
            clearLegacyCartStorage();
        } catch {
            // CartContext will retry claiming during post-login hydration.
        }
    }, []);

    const loginData = useCallback((token, expiresIn) => {
        setIsAuthenticated(true);
        setToken(token, expiresIn);
        setIsAuthModalOpen(false);
        
        fetchUser().then(async () => {
            await claimGuestCartAfterLogin();
            // Auto load / redirect to the pending customization page if initiated before login
            const pendingRedirect = sessionStorage.getItem("pending_redirect");
            if (pendingRedirect) {
                sessionStorage.removeItem("pending_redirect");
                window.location.href = pendingRedirect;
            }
        });
    }, [fetchUser, claimGuestCartAfterLogin]);

    const logout = useCallback(() => {
        setIsAuthenticated(false);
        setUser(null);
        authLogout();
    }, []);

    const requireAuth = useCallback((options = {}) => {
        if (!isAuthenticated) {
            const redirectPath = typeof options?.redirectPath === "string"
                ? options.redirectPath.trim()
                : "";
            if (redirectPath && typeof window !== "undefined") {
                sessionStorage.setItem("pending_redirect", redirectPath);
            }
            setAuthModalMode("login");
            setIsAuthModalOpen(true);
            return false;
        }
        return true;
    }, [isAuthenticated]);

    const showToast = useCallback((message, optionsOrDuration = 3000) => {
        const isOptions = typeof optionsOrDuration === 'object';
        const duration = isOptions ? (optionsOrDuration.duration ?? 3000) : optionsOrDuration;
        const type = isOptions ? optionsOrDuration.type : "default";
        const progress = isOptions ? optionsOrDuration.progress : undefined;
        const id = isOptions && optionsOrDuration.id ? optionsOrDuration.id : Date.now();

        setToastMessage({ message, id, type, progress, duration });

        // Hanya auto-hide jika bukan loading toast
        if (type !== 'loading') {
            setTimeout(() => {
                setToastMessage((prev) => prev?.id === id ? null : prev);
            }, duration);
        }
    }, []);

    const openAuthModal = useCallback((mode = "login") => {
        setAuthModalMode(mode);
        setIsAuthModalOpen(true);
    }, []);

    const openProfileSettings = useCallback(() => {
        if (!isAuthenticated) {
            openAuthModal("login");
            return;
        }

        setIsProfileSettingsOpen(true);
    }, [isAuthenticated, openAuthModal]);

    const closeAuthModal = useCallback(() => {
        setIsAuthModalOpen(false);
    }, []);

    const closeProfileSettings = useCallback(() => {
        setIsProfileSettingsOpen(false);
    }, []);
    
    const hideToast = useCallback(() => {
        setToastMessage(null);
    }, []);

    const canCheckout = hasFullyVerifiedContacts(user);

    return (
        <AuthContext.Provider value={{
            isAuthenticated,
            authReady,
            user,
            loginData,
            logout,
            refreshUser: fetchUser,
            isAuthModalOpen,
            authModalMode,
            openAuthModal,
            closeAuthModal,
            isProfileSettingsOpen,
            openProfileSettings,
            closeProfileSettings,
            canCheckout,
            requireAuth,
            toastMessage,
            showToast,
            hideToast
        }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        return {
            isAuthenticated: false,
            authReady: false,
            user: null,
            loginData: () => {},
            logout: () => {},
            refreshUser: async () => {},
            isAuthModalOpen: false,
            authModalMode: "login",
            openAuthModal: () => {},
            closeAuthModal: () => {},
            isProfileSettingsOpen: false,
            openProfileSettings: () => {},
            closeProfileSettings: () => {},
            canCheckout: false,
            requireAuth: () => false,
            toastMessage: null,
            showToast: () => {},
            hideToast: () => {}
        };
    }
    return context;
}
